import React, { useState, useMemo, useRef } from 'react';
import { 
  Sparkles, 
  Zap, 
  ShieldAlert, 
  DollarSign, 
  Receipt, 
  Scale, 
  ArrowRight, 
  ArrowUp,
  CheckCircle2, 
  Copy, 
  Check, 
  RotateCcw, 
  Sliders, 
  Building2, 
  FileText, 
  Clock, 
  ShieldCheck, 
  ExternalLink,
  ChevronRight,
  TrendingDown,
  Layers,
  FileSpreadsheet
} from 'lucide-react';

export type CrisisScenarioId = 'runway_shock' | 'trapped_receivable' | 'midnight_recon' | 'scope_creep';

interface FounderCrisisPromptDeckProps {
  onLaunchTrial: () => void;
  onOpenQuickStart?: () => void;
  onOpenReconciler?: () => void;
  onLoadDemo?: () => void;
  showToast?: (msg: string, type?: 'success' | 'info') => void;
}

interface JurisdictionConfig {
  name: string;
  code: string;
  taxRatePercent: number;
  vatRatePercent: number;
  statutoryInterestRate: number; // annual %
  taxAuthority: string;
  currencySymbol: string;
}

const JURISDICTIONS: Record<string, JurisdictionConfig> = {
  US: {
    name: 'United States (IRS / State)',
    code: 'US',
    taxRatePercent: 28, // Fed + Self-employment + Avg State
    vatRatePercent: 0,
    statutoryInterestRate: 18.0, // Commercial customary ~1.5%/mo
    taxAuthority: 'IRS Form 1040-ES / State Treasury',
    currencySymbol: '$'
  },
  UK: {
    name: 'United Kingdom (HMRC)',
    code: 'UK',
    taxRatePercent: 25, // Corp Tax
    vatRatePercent: 20, // Standard VAT
    statutoryInterestRate: 13.25, // Late Payment Act: 8% + Bank of England base rate (~5.25%)
    taxAuthority: 'HMRC CT600 / Making Tax Digital',
    currencySymbol: '£'
  },
  ZA: {
    name: 'South Africa (SARS)',
    code: 'ZA',
    taxRatePercent: 27, // Corporate Income Tax
    vatRatePercent: 15, // Standard VAT
    statutoryInterestRate: 11.75, // Prescribed Rate of Interest Act
    taxAuthority: 'SARS eFiling (IRP6 Provisional)',
    currencySymbol: 'R'
  },
  EU: {
    name: 'European Union (CIT / VAT)',
    code: 'EU',
    taxRatePercent: 24, // Avg EU CIT
    vatRatePercent: 21, // Avg EU VAT
    statutoryInterestRate: 12.0, // Late Payment Directive 2011/7/EU (8% + ECB rate)
    taxAuthority: 'EU Statutory Reverse Charge / VIES',
    currencySymbol: '€'
  },
  AU: {
    name: 'Australia (ATO)',
    code: 'AU',
    taxRatePercent: 25, // Small business base rate
    vatRatePercent: 10, // GST
    statutoryInterestRate: 11.38, // ATO General Interest Charge (GIC)
    taxAuthority: 'ATO Business Activity Statement (BAS)',
    currencySymbol: 'A$'
  }
};

const SAMPLE_RAW_STATEMENT_LINES = [
  { raw: 'STRIPE_PAYOUT_REF_99812_SETTLEMENT', amount: 18500, type: 'credit', category: 'Client Retainer Payout' },
  { raw: 'AWS_CLOUD_INFRA_US_EAST_1_V9201', amount: -420.50, type: 'debit', category: 'Software & Infrastructure' },
  { raw: 'GOOGLE*WORKSPACE_GSUITE_ANNUAL_SEATS', amount: -180.00, type: 'debit', category: 'Office Software' },
  { raw: 'WIRE_IN_ACME_CORP_MILESTONE_2_FINAL', amount: 9200, type: 'credit', category: 'Milestone Deliverable' },
  { raw: 'FUTURES_COFFEE_ROASTERS_MEETING_EXP', amount: -38.40, type: 'debit', category: 'Client Meals & Entertainment' },
  { raw: 'GITHUB_ENTERPRISE_COPILOT_SEATS', amount: -58.00, type: 'debit', category: 'Engineering Tools' },
  { raw: 'AIRBNB_OFFSITE_STRATEGY_RETREAT_ZA', amount: -840.00, type: 'debit', category: 'Operational Travel' },
  { raw: 'CLIENT_REFUND_DISPUTE_REVERSAL_TX', amount: -1500.00, type: 'debit', category: 'Commercial Adjustment' }
];

export function FounderCrisisPromptDeck({
  onLaunchTrial,
  onOpenQuickStart,
  onOpenReconciler,
  onLoadDemo,
  showToast
}: FounderCrisisPromptDeckProps) {
  const [activeScenario, setActiveScenario] = useState<CrisisScenarioId>('runway_shock');
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<string>('US');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [highlightedSimulator, setHighlightedSimulator] = useState<boolean>(false);
  
  const simulatorRef = useRef<HTMLDivElement>(null);
  const cardsDeckRef = useRef<HTMLDivElement>(null);

  // Auto-scrolls smoothly to the interactive live simulator so mobile founders instantly see results
  const handleSelectScenario = (scenarioId: CrisisScenarioId) => {
    setActiveScenario(scenarioId);
    setHighlightedSimulator(true);

    // Give React state a tick to mount the scenario tab, then scroll into view smoothly
    setTimeout(() => {
      if (simulatorRef.current) {
        simulatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        const el = document.getElementById('crisis-live-simulator');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 60);

    setTimeout(() => {
      setHighlightedSimulator(false);
    }, 2400);
  };

  // Quick navigation back up to the prompt card deck
  const handleScrollBackToCards = () => {
    if (cardsDeckRef.current) {
      cardsDeckRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      const el = document.getElementById('crisis-prompt-cards');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Scenario 1: Runway Shock States
  const [grossRevenue, setGrossRevenue] = useState<number>(42500);
  const [monthlyOpex, setMonthlyOpex] = useState<number>(4800);
  const [hasVatRegistered, setHasVatRegistered] = useState<boolean>(true);

  // Scenario 2: Trapped Receivable States
  const [invoiceAmount, setInvoiceAmount] = useState<number>(8500);
  const [daysOverdue, setDaysOverdue] = useState<number>(38);
  const [clientName, setClientName] = useState<string>('VenturePeak Media Ltd');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('INV-2026-088');

  // Scenario 3: Midnight Reconciliation States
  const [hasParsedStatement, setHasParsedStatement] = useState<boolean>(true);
  const [activeBankFeed, setActiveBankFeed] = useState<'stripe_chase' | 'fnb_capitec' | 'wise_revolut'>('stripe_chase');

  // Scenario 4: Scope Creep Kill-Fee States
  const [originalSowValue, setOriginalSowValue] = useState<number>(14000);
  const [scopeAddition, setScopeAddition] = useState<string>('Custom multi-tenant billing gateway + 3 supplementary landing funnels');
  const [enforcementStance, setEnforcementStance] = useState<'diplomatic' | 'direct' | 'strict'>('direct');

  const jur = JURISDICTIONS[selectedJurisdiction] || JURISDICTIONS.US;
  const sym = jur.currencySymbol;

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    if (showToast) {
      showToast('Copied proforma to clipboard!', 'success');
    }
    setTimeout(() => setCopiedKey(null), 2500);
  };

  // Calculations for Scenario 1: False Runway
  const runwayCalculations = useMemo(() => {
    const taxBufferPercent = jur.taxRatePercent;
    const vatBufferPercent = hasVatRegistered ? jur.vatRatePercent : 0;
    const totalStatutoryWithholdingPercent = taxBufferPercent + (vatBufferPercent > 0 ? (vatBufferPercent / (100 + vatBufferPercent)) * 100 : 0);
    
    const taxWithholdingAmount = (grossRevenue * (taxBufferPercent / 100));
    const vatWithholdingAmount = hasVatRegistered ? (grossRevenue * (jur.vatRatePercent / (100 + jur.vatRatePercent))) : 0;
    const totalStatutoryRingFence = taxWithholdingAmount + vatWithholdingAmount;

    const netAfterTaxes = Math.max(0, grossRevenue - totalStatutoryRingFence);
    const safeOwnerDraw = Math.max(0, netAfterTaxes - monthlyOpex);
    const coMinglingDangerPercentage = Math.round((totalStatutoryRingFence / grossRevenue) * 100);

    return {
      taxWithholdingAmount,
      vatWithholdingAmount,
      totalStatutoryRingFence,
      safeOwnerDraw,
      netAfterTaxes,
      coMinglingDangerPercentage
    };
  }, [grossRevenue, monthlyOpex, hasVatRegistered, jur]);

  // Calculations for Scenario 2: Trapped Receivable
  const receivableCalculations = useMemo(() => {
    const annualRate = jur.statutoryInterestRate;
    const dailyRate = (annualRate / 100) / 365;
    const accruedInterest = invoiceAmount * dailyRate * daysOverdue;
    const statutoryCompensationFee = jur.code === 'UK' ? 70 : (jur.code === 'EU' ? 40 : 50); // Statutory debt recovery fee
    const totalEnforceableClaim = invoiceAmount + accruedInterest + statutoryCompensationFee;

    const cureNoticeProforma = `FORMAL NOTICE OF DEMAND & STATUTORY INTEREST ASSESSMENT
Date: October 28, 2026
To: Accounts Payable / Chief Financial Officer, ${clientName}
Reference: Overdue Invoice ${invoiceNumber} (Matured: ${daysOverdue} Days Ago)
Authority Preset: ${jur.name} (${jur.taxAuthority})

Dear Accounts Team,

We hereby issue formal notice that Invoice ${invoiceNumber} in the principal amount of ${sym}${invoiceAmount.toLocaleString()} remains past due for thirty-eight (${daysOverdue}) days, exceeding all contractual settlement windows.

Under standard commercial solvency regulations (${jur.code === 'UK' ? 'UK Late Payment of Commercial Debts Act 1998' : jur.code === 'EU' ? 'EU Directive 2011/7/EU' : 'Uniform Commercial Code / Statutory Commercial Precedents'}), we have applied standard statutory late settlement interest:

1. Principal Overdue Amount: ${sym}${invoiceAmount.toLocaleString()}
2. Accrued Statutory Interest (${annualRate}% p.a. for ${daysOverdue} days): ${sym}${accruedInterest.toFixed(2)}
3. Commercial Recovery Assessment Fee: ${sym}${statutoryCompensationFee.toFixed(2)}
----------------------------------------------------------------------
TOTAL ENFORCEABLE SETTLEMENT DEMAND: ${sym}${totalEnforceableClaim.toFixed(2)}

Please remit payment in full within three (3) business days to forestall administrative escalation and statutory credit bureau recording. Remittance instructions remain identical to the original proforma.

Kind regards,
AzuraOS Sovereign Cash Command on behalf of Founders`;

    return {
      accruedInterest,
      statutoryCompensationFee,
      totalEnforceableClaim,
      cureNoticeProforma,
      annualRate
    };
  }, [invoiceAmount, daysOverdue, clientName, invoiceNumber, jur, sym]);

  // Calculations for Scenario 4: Scope Creep Kill-Fee
  const scopeCalculations = useMemo(() => {
    const changeOrderValue = Math.round(originalSowValue * 0.35); // 35% typical scope addition
    const killFeeRetention = Math.round(originalSowValue * 0.40); // 40% work-in-progress retainer indemnity
    const totalRevisedValue = originalSowValue + changeOrderValue;

    const stanceIntro = enforcementStance === 'diplomatic'
      ? `We love the ambition of this project and want to ensure these additional features deliver extraordinary results without diluting our core delivery timeline.`
      : enforcementStance === 'strict'
      ? `Per Section 4.2 of our signed Master Services Agreement, any modification beyond Schedule A requires immediate change-order signoff and advance capitalization.`
      : `To incorporate these valuable additions while maintaining the approved milestone schedule, we have prepared the following Change-Order Addendum.`;

    const scopeMemoProforma = `OFFICIAL CHANGE-ORDER SPECIFICATION & SCOPE DEFENSE PROFORMA
Project Reference: Master Services Agreement / Schedule A Delivery
Client Counterparty: Client Delivery Lead
Adjustment Classification: Unscheduled Scope Expansion (${scopeAddition})

${stanceIntro}

1. Scope Inclusions:
   - Base Contract Scope Value: ${sym}${originalSowValue.toLocaleString()}
   - Supplementary Scope Scope Item: "${scopeAddition}"
   - Calculated Engineering & Strategic Surcharge: +${sym}${changeOrderValue.toLocaleString()}
   -----------------------------------------------------------------
   REVISED PROJECT INVESTMENT: ${sym}${totalRevisedValue.toLocaleString()}

2. Kill-Fee & Mutual Indemnity Term:
   If the counterparty elects to cancel or pause the project during this supplemental sprint, a non-negotiable Kill-Fee of ${sym}${killFeeRetention.toLocaleString()} (representing accrued labor & reserved sprint bandwidth) becomes payable within 5 business days.

Executed via AzuraOS Sovereign Operations Framework.`;

    return {
      changeOrderValue,
      killFeeRetention,
      totalRevisedValue,
      scopeMemoProforma
    };
  }, [originalSowValue, scopeAddition, enforcementStance, sym]);

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6 text-left">
      
      {/* 🌟 1. EXECUTIVE PRESENCE BANNER (Zero Glare, Rock-Solid GPU Stability) */}
      <div className="relative rounded-3xl bg-[#0E1214] border border-teal-500/40 p-5 sm:p-7 shadow-xl">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#1E2628] pb-5">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/15 border border-teal-500/30 rounded-full text-xs font-mono font-bold text-teal-300">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              <span>SOVEREIGN OPERATIONS COMMAND • ONLINE &amp; READY</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-black text-white tracking-tight">
              Welcome to AzuraOS. <span className="text-[#14B8A6]">Your Ops Deck on Standby.</span>
            </h2>
            <p className="text-xs sm:text-sm text-gray-300 max-w-2xl font-normal leading-relaxed">
              Don’t take marketing promises on faith. Select a high-stakes founder crisis below to experience our autonomous calculations, statutory tax airlock, and proforma generation in real time—right here, before touching signup.
            </p>
          </div>

          {/* Quick Global Jurisdiction Switcher */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 bg-[#0A0D0E] border border-[#212729] rounded-2xl p-2 shrink-0">
            <span className="text-[11px] font-mono text-gray-400 px-2 flex items-center gap-1.5">
              <Scale size={13} className="text-teal-400" />
              Statutory Preset:
            </span>
            <select
              value={selectedJurisdiction}
              onChange={(e) => setSelectedJurisdiction(e.target.value)}
              className="bg-[#141819] border border-[#2A3336] rounded-xl text-xs font-bold text-teal-300 px-3 py-1.5 outline-none cursor-pointer focus:border-teal-400 transition-colors"
            >
              <option value="US">🇺🇸 United States (IRS)</option>
              <option value="UK">🇬🇧 United Kingdom (HMRC)</option>
              <option value="ZA">🇿🇦 South Africa (SARS)</option>
              <option value="EU">🇪🇺 European Union (CIT/VAT)</option>
              <option value="AU">🇦🇺 Australia (ATO)</option>
            </select>
          </div>
        </div>

        {/* 🎴 2. THE 4 FOUNDER CRISIS PROMPT CARDS (INTERACTIVE SELECTORS) */}
        <div 
          ref={cardsDeckRef}
          id="crisis-prompt-cards"
          className="pt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 scroll-mt-6"
        >
          
          {/* Card 1: False Runway */}
          <button
            type="button"
            onClick={() => handleSelectScenario('runway_shock')}
            className={`group text-left p-4 rounded-2xl border transition-all duration-200 cursor-pointer relative flex flex-col justify-between ${
              activeScenario === 'runway_shock'
                ? 'bg-[#151D1F] border-teal-400 ring-1 ring-teal-400/80 shadow-md'
                : 'bg-[#101415] border-[#22292C] hover:border-teal-500/50 hover:bg-[#131819]'
            }`}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-teal-400 px-2 py-0.5 bg-teal-500/10 border border-teal-500/25 rounded-md">
                  CFO &amp; Treasury
                </span>
                <span className="text-amber-400 text-xs font-mono font-bold">⚡ Crisis 01</span>
              </div>
              <h3 className="text-sm font-bold text-white group-hover:text-teal-200 transition-colors leading-snug">
                "I have {sym}42k in gross bank receipts. How much can I safely withdraw?"
              </h3>
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Prevents catastrophic co-mingling tax spend. Automatically ring-fences {jur.taxAuthority} buffers.
              </p>
            </div>
            <div className="pt-3 flex items-center justify-between text-[11px] font-mono">
              <span className={`font-semibold flex items-center gap-1 ${activeScenario === 'runway_shock' ? 'text-teal-300' : 'text-gray-400 group-hover:text-teal-300'}`}>
                {activeScenario === 'runway_shock' ? '● Simulating Live ↓' : 'Click to Test ↓'}
              </span>
              <span className="text-xs text-teal-400 font-bold">{sym}13.6k Tax Buffer</span>
            </div>
          </button>

          {/* Card 2: Trapped Receivable */}
          <button
            type="button"
            onClick={() => handleSelectScenario('trapped_receivable')}
            className={`group text-left p-4 rounded-2xl border transition-all duration-200 cursor-pointer relative flex flex-col justify-between ${
              activeScenario === 'trapped_receivable'
                ? 'bg-[#191815] border-amber-400 ring-1 ring-amber-400/80 shadow-md'
                : 'bg-[#101415] border-[#22292C] hover:border-amber-500/50 hover:bg-[#151412]'
            }`}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-amber-400 px-2 py-0.5 bg-amber-500/10 border border-amber-500/25 rounded-md">
                  Cash Flow Defense
                </span>
                <span className="text-amber-400 text-xs font-mono font-bold">💸 Crisis 02</span>
              </div>
              <h3 className="text-sm font-bold text-white group-hover:text-amber-200 transition-colors leading-snug">
                "Client invoice is 38 days overdue. Draft an enforceable cure notice."
              </h3>
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Applies statutory commercial interest rates ({jur.statutoryInterestRate}% p.a.) &amp; legal recovery fees.
              </p>
            </div>
            <div className="pt-3 flex items-center justify-between text-[11px] font-mono">
              <span className={`font-semibold flex items-center gap-1 ${activeScenario === 'trapped_receivable' ? 'text-amber-300' : 'text-gray-400 group-hover:text-amber-300'}`}>
                {activeScenario === 'trapped_receivable' ? '● Simulating Live ↓' : 'Click to Test ↓'}
              </span>
              <span className="text-xs text-amber-400 font-bold">+{sym}442 Interest</span>
            </div>
          </button>

          {/* Card 3: Midnight Reconciliation */}
          <button
            type="button"
            onClick={() => handleSelectScenario('midnight_recon')}
            className={`group text-left p-4 rounded-2xl border transition-all duration-200 cursor-pointer relative flex flex-col justify-between ${
              activeScenario === 'midnight_recon'
                ? 'bg-[#151A1D] border-cyan-400 ring-1 ring-cyan-400/80 shadow-md'
                : 'bg-[#101415] border-[#22292C] hover:border-cyan-500/50 hover:bg-[#121618]'
            }`}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-cyan-400 px-2 py-0.5 bg-cyan-500/10 border border-cyan-500/25 rounded-md">
                  Air-Gapped Parser
                </span>
                <span className="text-cyan-400 text-xs font-mono font-bold">🧾 Crisis 03</span>
              </div>
              <h3 className="text-sm font-bold text-white group-hover:text-cyan-200 transition-colors leading-snug">
                "Accountant needs books tonight. Parse messy bank records in 0.05s."
              </h3>
              <p className="text-[11px] text-gray-400 leading-relaxed">
                No Plaid credentials. Zero model training. Instant audit categorization and CSV export.
              </p>
            </div>
            <div className="pt-3 flex items-center justify-between text-[11px] font-mono">
              <span className={`font-semibold flex items-center gap-1 ${activeScenario === 'midnight_recon' ? 'text-cyan-300' : 'text-gray-400 group-hover:text-cyan-300'}`}>
                {activeScenario === 'midnight_recon' ? '● Simulating Live ↓' : 'Click to Test ↓'}
              </span>
              <span className="text-xs text-cyan-400 font-bold">100% Sovereign</span>
            </div>
          </button>

          {/* Card 4: Scope Creep Kill-Fee */}
          <button
            type="button"
            onClick={() => handleSelectScenario('scope_creep')}
            className={`group text-left p-4 rounded-2xl border transition-all duration-200 cursor-pointer relative flex flex-col justify-between ${
              activeScenario === 'scope_creep'
                ? 'bg-[#1A161F] border-purple-400 ring-1 ring-purple-400/80 shadow-md'
                : 'bg-[#101415] border-[#22292C] hover:border-purple-500/50 hover:bg-[#161219]'
            }`}
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-purple-400 px-2 py-0.5 bg-purple-500/10 border border-purple-500/25 rounded-md">
                  Chief of Staff SOW Shield
                </span>
                <span className="text-purple-400 text-xs font-mono font-bold">🛡️ Crisis 04</span>
              </div>
              <h3 className="text-sm font-bold text-white group-hover:text-purple-200 transition-colors leading-snug">
                "Client demands extra features mid-sprint. Generate a scope boundary memo."
              </h3>
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Non-sycophantic change-order defense proforma with enforceable kill-fee protection clauses.
              </p>
            </div>
            <div className="pt-3 flex items-center justify-between text-[11px] font-mono">
              <span className={`font-semibold flex items-center gap-1 ${activeScenario === 'scope_creep' ? 'text-purple-300' : 'text-gray-400 group-hover:text-purple-300'}`}>
                {activeScenario === 'scope_creep' ? '● Simulating Live ↓' : 'Click to Test ↓'}
              </span>
              <span className="text-xs text-purple-400 font-bold">+{sym}4.9k Change Order</span>
            </div>
          </button>

        </div>

        {/* 🔬 3. LIVE INTERACTIVE SIMULATOR VIEWPORT */}
        <div 
          ref={simulatorRef}
          id="crisis-live-simulator"
          className={`mt-6 p-4 sm:p-6 bg-[#080B0C] border rounded-2xl space-y-6 transition-all duration-300 scroll-mt-6 ${
            highlightedSimulator
              ? activeScenario === 'runway_shock'
                ? 'border-teal-400 ring-2 ring-teal-400/80 shadow-lg shadow-teal-500/10'
                : activeScenario === 'trapped_receivable'
                ? 'border-amber-400 ring-2 ring-amber-400/80 shadow-lg shadow-amber-500/10'
                : activeScenario === 'midnight_recon'
                ? 'border-cyan-400 ring-2 ring-cyan-400/80 shadow-lg shadow-cyan-500/10'
                : 'border-purple-400 ring-2 ring-purple-400/80 shadow-lg shadow-purple-500/10'
              : 'border-[#1E2629]'
          }`}
        >
          {/* Quick Scenario Navigation Bar for Mobile + Back to Cards Button */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#1A2224] pb-3">
            <div className="flex items-center gap-1.5 overflow-x-auto py-1 max-w-full no-scrollbar text-xs font-mono">
              <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mr-1 shrink-0">Switch:</span>
              <button
                type="button"
                onClick={() => handleSelectScenario('runway_shock')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer shrink-0 flex items-center gap-1 ${
                  activeScenario === 'runway_shock' 
                    ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40' 
                    : 'bg-[#121618] text-gray-400 hover:text-white border border-[#232C2F]'
                }`}
              >
                <span>⚡ 01. Safe Tax Draw</span>
              </button>
              <button
                type="button"
                onClick={() => handleSelectScenario('trapped_receivable')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer shrink-0 flex items-center gap-1 ${
                  activeScenario === 'trapped_receivable' 
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' 
                    : 'bg-[#121618] text-gray-400 hover:text-white border border-[#232C2F]'
                }`}
              >
                <span>💸 02. Overdue Invoice</span>
              </button>
              <button
                type="button"
                onClick={() => handleSelectScenario('midnight_recon')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer shrink-0 flex items-center gap-1 ${
                  activeScenario === 'midnight_recon' 
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40' 
                    : 'bg-[#121618] text-gray-400 hover:text-white border border-[#232C2F]'
                }`}
              >
                <span>🧾 03. Clean Recon</span>
              </button>
              <button
                type="button"
                onClick={() => handleSelectScenario('scope_creep')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer shrink-0 flex items-center gap-1 ${
                  activeScenario === 'scope_creep' 
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40' 
                    : 'bg-[#121618] text-gray-400 hover:text-white border border-[#232C2F]'
                }`}
              >
                <span>🛡️ 04. Scope SOW</span>
              </button>
            </div>

            <button
              type="button"
              onClick={handleScrollBackToCards}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#141A1C] hover:bg-[#1D2528] border border-[#283236] rounded-lg text-[11px] font-mono text-gray-300 hover:text-teal-300 transition-colors cursor-pointer shrink-0 ml-auto"
              title="Scroll back up to crisis prompt cards"
            >
              <ArrowUp size={12} className="text-teal-400" />
              <span>Back to Cards</span>
            </button>
          </div>
          
          {/* SIMULATOR 1: FALSE RUNWAY SHOCK */}
          {activeScenario === 'runway_shock' && (
            <div className="space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1A2224] pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold text-teal-400 uppercase tracking-wider">
                      Interactive Simulator 01
                    </span>
                    <span className="text-[11px] text-gray-400">• Real-Time Cash Airlock</span>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-white">
                    The False Runway Shock: Gross Revenue vs True Safe Draw
                  </h4>
                </div>
                <div className="text-xs font-mono text-teal-300 bg-teal-500/10 border border-teal-500/25 px-3 py-1 rounded-xl shrink-0">
                  Preset: {jur.name}
                </div>
              </div>

              {/* Reactive Controls Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Control 1: Gross Cash in Bank */}
                <div className="p-4 bg-[#101416] border border-[#232C2F] rounded-xl space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-gray-300">
                      Gross Collected Receipts (In Bank):
                    </label>
                    <span className="text-sm font-mono font-bold text-teal-400">
                      {sym}{grossRevenue.toLocaleString()}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="10000"
                    max="120000"
                    step="2500"
                    value={grossRevenue}
                    onChange={(e) => setGrossRevenue(Number(e.target.value))}
                    className="w-full accent-teal-400 cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] font-mono text-gray-500">
                    <span>{sym}10,000</span>
                    <span>{sym}65,000</span>
                    <span>{sym}120,000</span>
                  </div>
                </div>

                {/* Control 2: Monthly Operating Overheads */}
                <div className="p-4 bg-[#101416] border border-[#232C2F] rounded-xl space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-gray-300">
                      Fixed Monthly Operating Expenses:
                    </label>
                    <span className="text-sm font-mono font-bold text-purple-400">
                      {sym}{monthlyOpex.toLocaleString()}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1000"
                    max="20000"
                    step="500"
                    value={monthlyOpex}
                    onChange={(e) => setMonthlyOpex(Number(e.target.value))}
                    className="w-full accent-purple-400 cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] font-mono text-gray-500">
                    <span>{sym}1,000</span>
                    <span>{sym}10,000</span>
                    <span>{sym}20,000</span>
                  </div>
                </div>
              </div>

              {/* Real-time Calculation Breakdown Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 font-mono">
                
                {/* Metric 1: Total Inflow */}
                <div className="p-3.5 bg-[#0F1416] border border-[#222B2E] rounded-xl space-y-1">
                  <span className="text-[11px] text-gray-400">Gross Account Balance</span>
                  <div className="text-lg font-bold text-white">
                    {sym}{grossRevenue.toLocaleString()}
                  </div>
                  <span className="text-[10px] text-gray-400 block">Unadjusted raw cash</span>
                </div>

                {/* Metric 2: Statutory Tax Airlock Buffer */}
                <div className="p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-xl space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-amber-300">Statutory Tax Ring-Fence</span>
                    <span className="text-[10px] px-1.5 py-0.2 bg-amber-500/20 text-amber-200 rounded">
                      Locked
                    </span>
                  </div>
                  <div className="text-lg font-bold text-amber-400">
                    -{sym}{Math.round(runwayCalculations.totalStatutoryRingFence).toLocaleString()}
                  </div>
                  <span className="text-[10px] text-amber-200/80 block">
                    {jur.taxAuthority} ({jur.taxRatePercent}% CIT{jur.vatRatePercent > 0 ? ` + ${jur.vatRatePercent}% VAT` : ''})
                  </span>
                </div>

                {/* Metric 3: Safe Founder Draw */}
                <div className="p-3.5 bg-emerald-500/10 border border-emerald-500/30 rounded-xl space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-emerald-300">True Safe Owner Draw</span>
                    <span className="text-[10px] px-1.5 py-0.2 bg-emerald-500/20 text-emerald-300 rounded font-bold">
                      Safe
                    </span>
                  </div>
                  <div className="text-lg font-bold text-emerald-400">
                    {sym}{Math.round(runwayCalculations.safeOwnerDraw).toLocaleString()}
                  </div>
                  <span className="text-[10px] text-emerald-200/80 block">
                    Zero co-mingling liability
                  </span>
                </div>

              </div>

              {/* Visual Runway Ratio Bar */}
              <div className="space-y-1.5">
                <div className="flex justify-between text-[11px] font-mono text-gray-400">
                  <span>Balance Capital Allocation Breakdown:</span>
                  <span className="text-amber-400 font-bold">
                    ⚠️ {runwayCalculations.coMinglingDangerPercentage}% of gross funds belong to tax authorities
                  </span>
                </div>
                <div className="w-full h-3 rounded-full bg-[#182022] overflow-hidden flex">
                  <div 
                    style={{ width: `${runwayCalculations.coMinglingDangerPercentage}%` }} 
                    className="h-full bg-amber-500/80 transition-all duration-300" 
                    title="Statutory Tax Buffer"
                  />
                  <div 
                    style={{ width: `${Math.round((monthlyOpex / grossRevenue) * 100)}%` }} 
                    className="h-full bg-purple-500/80 transition-all duration-300" 
                    title="Operating Expenses"
                  />
                  <div 
                    style={{ width: `${Math.max(0, 100 - runwayCalculations.coMinglingDangerPercentage - Math.round((monthlyOpex / grossRevenue) * 100))}%` }} 
                    className="h-full bg-emerald-500 transition-all duration-300" 
                    title="Safe Owner Draw"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-4 text-[10px] font-mono text-gray-400 pt-0.5">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-400" /> Ring-Fenced Tax ({sym}{Math.round(runwayCalculations.totalStatutoryRingFence).toLocaleString()})
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-purple-400" /> Fixed Opex ({sym}{monthlyOpex.toLocaleString()})
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" /> Safe Owner Draw ({sym}{Math.round(runwayCalculations.safeOwnerDraw).toLocaleString()})
                  </span>
                </div>
              </div>

              {/* Chief of Staff Tactical Insight */}
              <div className="p-3.5 bg-[#0C1011] border-l-4 border-teal-400 rounded-r-xl text-xs text-gray-300 leading-relaxed space-y-1">
                <span className="text-teal-400 font-bold font-mono text-[11px] block">
                  AZURA CHIEF OF STAFF RADAR DEBRIEF:
                </span>
                <p>
                  Most founders withdraw from the raw {sym}{grossRevenue.toLocaleString()} balance, mistaking gross receipts for disposable equity. When seasonal provisional tax filings hit, they discover a {sym}{Math.round(runwayCalculations.totalStatutoryRingFence).toLocaleString()} statutory deficit. AzuraOS enforces this ring-fence locally without touching your bank credentials.
                </p>
              </div>
            </div>
          )}

          {/* SIMULATOR 2: TRAPPED RECEIVABLE */}
          {activeScenario === 'trapped_receivable' && (
            <div className="space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1A2224] pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold text-amber-400 uppercase tracking-wider">
                      Interactive Simulator 02
                    </span>
                    <span className="text-[11px] text-gray-400">• Enforceable Cash Recovery</span>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-white">
                    The {sym}8,500 Trapped Receivable: Statutory Cure Notice with Interest
                  </h4>
                </div>
                <div className="text-xs font-mono text-amber-300 bg-amber-500/10 border border-amber-500/25 px-3 py-1 rounded-xl shrink-0">
                  Statutory Rate: {receivableCalculations.annualRate}% p.a.
                </div>
              </div>

              {/* Controls */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 bg-[#101416] border border-[#232C2F] rounded-xl space-y-1">
                  <label className="text-[11px] font-semibold text-gray-400">Invoice Value:</label>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-mono text-gray-400">{sym}</span>
                    <input
                      type="number"
                      value={invoiceAmount}
                      onChange={(e) => setInvoiceAmount(Math.max(500, Number(e.target.value)))}
                      className="w-full bg-[#171E20] border border-[#2C383C] rounded-lg px-2.5 py-1 text-sm font-mono font-bold text-white outline-none focus:border-amber-400"
                    />
                  </div>
                </div>

                <div className="p-3 bg-[#101416] border border-[#232C2F] rounded-xl space-y-1">
                  <div className="flex justify-between">
                    <label className="text-[11px] font-semibold text-gray-400">Days Past Due:</label>
                    <span className="text-xs font-mono font-bold text-amber-400">{daysOverdue} Days</span>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max="90"
                    value={daysOverdue}
                    onChange={(e) => setDaysOverdue(Number(e.target.value))}
                    className="w-full accent-amber-400 cursor-pointer pt-1"
                  />
                </div>

                <div className="p-3 bg-[#101416] border border-[#232C2F] rounded-xl space-y-1">
                  <label className="text-[11px] font-semibold text-gray-400">Debtor Counterparty:</label>
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="w-full bg-[#171E20] border border-[#2C383C] rounded-lg px-2.5 py-1 text-xs font-mono font-bold text-white outline-none focus:border-amber-400"
                  />
                </div>
              </div>

              {/* Notice Preview with Copy */}
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <span className="text-xs font-mono font-bold text-gray-300 flex items-center gap-1.5">
                    <FileText size={13} className="text-amber-400 shrink-0" />
                    <span>Generated Statutory Cure Notice Proforma:</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopy(receivableCalculations.cureNoticeProforma, 'cure_notice')}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 rounded-lg text-xs font-mono font-bold cursor-pointer transition-colors active:scale-95 shrink-0 self-start sm:self-auto"
                  >
                    {copiedKey === 'cure_notice' ? (
                      <>
                        <Check size={13} className="text-emerald-400" />
                        <span>Copied Proforma!</span>
                      </>
                    ) : (
                      <>
                        <Copy size={13} />
                        <span>Copy Proforma Notice</span>
                      </>
                    )}
                  </button>
                </div>

                <pre className="p-3.5 bg-[#060809] border border-[#222B2E] rounded-xl text-[11px] font-mono text-gray-300 leading-relaxed overflow-x-auto max-h-48 whitespace-pre-wrap select-all">
                  {receivableCalculations.cureNoticeProforma}
                </pre>
              </div>

              {/* Debt Summary Pill Bar */}
              <div className="p-3 bg-[#0F1416] border border-[#222C2F] rounded-xl flex flex-wrap items-center justify-between gap-3 text-xs font-mono">
                <div>
                  <span className="text-gray-400">Base Debt: </span>
                  <span className="text-white font-bold">{sym}{invoiceAmount.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-400">Accrued Interest: </span>
                  <span className="text-amber-400 font-bold">+{sym}{receivableCalculations.accruedInterest.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-gray-400">Statutory Fee: </span>
                  <span className="text-amber-400 font-bold">+{sym}{receivableCalculations.statutoryCompensationFee.toFixed(2)}</span>
                </div>
                <div className="text-sm font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 rounded-lg">
                  Total Enforceable: {sym}{receivableCalculations.totalEnforceableClaim.toFixed(2)}
                </div>
              </div>
            </div>
          )}

          {/* SIMULATOR 3: MIDNIGHT RECONCILIATION */}
          {activeScenario === 'midnight_recon' && (
            <div className="space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1A2224] pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-wider">
                      Interactive Simulator 03
                    </span>
                    <span className="text-[11px] text-gray-400">• Air-Gapped Ingestion</span>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-white">
                    Midnight Reconciliation: 0.05-Second Sovereign Statement Ingestion
                  </h4>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-cyan-300 bg-cyan-500/10 border border-cyan-500/25 px-2.5 py-1 rounded-xl">
                    ⚡ Parse Latency: 48ms
                  </span>
                </div>
              </div>

              {/* Feed Switcher Box */}
              <div className="p-3 bg-[#101416] border border-[#232C2F] rounded-xl space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-gray-400 flex items-center gap-1.5">
                    <Sliders size={12} className="text-cyan-400" />
                    Select Sample Bank Feed:
                  </span>
                  <span className="text-[10px] font-mono text-cyan-300/80">Air-gapped local parser</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveBankFeed('stripe_chase')}
                    className={`py-2 px-2 text-xs font-mono rounded-lg border transition-all cursor-pointer text-center ${
                      activeBankFeed === 'stripe_chase'
                        ? 'bg-cyan-500/25 border-cyan-400 text-cyan-200 font-bold ring-1 ring-cyan-400/50'
                        : 'bg-[#171E20] border-[#2C383C] text-gray-400 hover:text-white hover:bg-[#1C2528]'
                    }`}
                  >
                    Stripe + Chase
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveBankFeed('fnb_capitec')}
                    className={`py-2 px-2 text-xs font-mono rounded-lg border transition-all cursor-pointer text-center ${
                      activeBankFeed === 'fnb_capitec'
                        ? 'bg-cyan-500/25 border-cyan-400 text-cyan-200 font-bold ring-1 ring-cyan-400/50'
                        : 'bg-[#171E20] border-[#2C383C] text-gray-400 hover:text-white hover:bg-[#1C2528]'
                    }`}
                  >
                    SA FNB / Standard Bank
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveBankFeed('wise_revolut')}
                    className={`py-2 px-2 text-xs font-mono rounded-lg border transition-all cursor-pointer text-center ${
                      activeBankFeed === 'wise_revolut'
                        ? 'bg-cyan-500/25 border-cyan-400 text-cyan-200 font-bold ring-1 ring-cyan-400/50'
                        : 'bg-[#171E20] border-[#2C383C] text-gray-400 hover:text-white hover:bg-[#1C2528]'
                    }`}
                  >
                    Wise &amp; Revolut
                  </button>
                </div>
              </div>

              {/* Parsed Audit Table */}
              <div className="overflow-x-auto rounded-xl border border-[#1F272A] bg-[#070A0B]">
                <table className="w-full text-left text-xs font-mono">
                  <thead className="bg-[#111618] text-gray-400 border-b border-[#1F272A] text-[10px] uppercase">
                    <tr>
                      <th className="p-2.5">Raw Bank Feed String</th>
                      <th className="p-2.5">Audit Classification</th>
                      <th className="p-2.5 text-right">Amount</th>
                      <th className="p-2.5 text-center">Tax Treatment</th>
                      <th className="p-2.5 text-center">Audit Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#171E20] text-gray-300">
                    {SAMPLE_RAW_STATEMENT_LINES.map((row, idx) => (
                      <tr key={idx} className="hover:bg-[#0D1214] transition-colors">
                        <td className="p-2.5 font-bold text-gray-200 truncate max-w-[220px]" title={row.raw}>
                          {row.raw}
                        </td>
                        <td className="p-2.5 text-teal-300">
                          {row.category}
                        </td>
                        <td className={`p-2.5 text-right font-bold ${row.amount > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {row.amount > 0 ? `+${sym}${row.amount.toLocaleString()}` : `-${sym}${Math.abs(row.amount).toLocaleString()}`}
                        </td>
                        <td className="p-2.5 text-center">
                          <span className="px-2 py-0.5 rounded text-[10px] bg-[#161E21] text-gray-300 border border-[#243136]">
                            {row.amount > 0 ? 'Taxable Revenue' : 'Deductible Opex'}
                          </span>
                        </td>
                        <td className="p-2.5 text-center">
                          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400">
                            <CheckCircle2 size={11} /> 100% Reconciled
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Export action */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs font-mono text-gray-400">
                <span className="flex items-center gap-1.5 text-gray-300">
                  <ShieldCheck size={14} className="text-emerald-400" />
                  Processed strictly in browser client memory. Zero Plaid credentials needed.
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const csvContent = "data:text/csv;charset=utf-8," 
                      + "Raw String,Category,Amount,Tax Status\n" 
                      + SAMPLE_RAW_STATEMENT_LINES.map(e => `"${e.raw}","${e.category}",${e.amount},${e.amount > 0 ? 'Revenue' : 'Deductible'}`).join("\n");
                    const encodedUri = encodeURI(csvContent);
                    const link = document.createElement("a");
                    link.setAttribute("href", encodedUri);
                    link.setAttribute("download", `azura_accountant_audit_pack_${jur.code.toLowerCase()}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    if (showToast) showToast('Generated & downloaded Accountant Audit CSV!', 'success');
                  }}
                  className="px-3.5 py-1.5 bg-[#141A1C] hover:bg-[#1E2528] border border-[#2B363A] text-cyan-300 hover:text-white rounded-xl flex items-center gap-2 cursor-pointer transition-colors active:scale-95"
                >
                  <FileSpreadsheet size={13} />
                  <span>Download Accountant-Ready CSV</span>
                </button>
              </div>
            </div>
          )}

          {/* SIMULATOR 4: SCOPE CREEP KILL-FEE */}
          {activeScenario === 'scope_creep' && (
            <div className="space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1A2224] pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold text-purple-400 uppercase tracking-wider">
                      Interactive Simulator 04
                    </span>
                    <span className="text-[11px] text-gray-400">• Anti-Sycophancy SOW Shield</span>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-white">
                    The Scope Creep Kill-Fee: Assertive SOW Defense &amp; Change Order Proforma
                  </h4>
                </div>
                <div className="text-xs font-mono text-purple-300 bg-purple-500/10 border border-purple-500/25 px-3 py-1 rounded-xl shrink-0">
                  Kill-Fee Defense: 40% Retention
                </div>
              </div>

              {/* Controls */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 bg-[#101416] border border-[#232C2F] rounded-xl space-y-1">
                  <label className="text-[11px] font-semibold text-gray-400">Original SOW Value:</label>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-mono text-gray-400">{sym}</span>
                    <input
                      type="number"
                      value={originalSowValue}
                      onChange={(e) => setOriginalSowValue(Math.max(1000, Number(e.target.value)))}
                      className="w-full bg-[#171E20] border border-[#2C383C] rounded-lg px-2.5 py-1 text-sm font-mono font-bold text-white outline-none focus:border-purple-400"
                    />
                  </div>
                </div>

                <div className="p-3 bg-[#101416] border border-[#232C2F] rounded-xl space-y-1 sm:col-span-2">
                  <label className="text-[11px] font-semibold text-gray-400">Unscheduled Client Request:</label>
                  <input
                    type="text"
                    value={scopeAddition}
                    onChange={(e) => setScopeAddition(e.target.value)}
                    className="w-full bg-[#171E20] border border-[#2C383C] rounded-lg px-2.5 py-1 text-xs font-mono text-white outline-none focus:border-purple-400"
                  />
                </div>

                {/* Stance Toggle Box (100% contained within matching box) */}
                <div className="p-3 bg-[#101416] border border-[#232C2F] rounded-xl space-y-2 sm:col-span-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                    <label className="text-[11px] font-semibold text-gray-400 flex items-center gap-1.5">
                      <Sliders size={12} className="text-purple-400" />
                      Executive Enforcement Stance:
                    </label>
                    <span className="text-[10px] font-mono text-purple-300">
                      {enforcementStance === 'diplomatic' ? 'Client-friendly boundary memo' : enforcementStance === 'direct' ? 'Standard contractual addendum' : 'Strict MSA Section 4.2 enforcement'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {(['diplomatic', 'direct', 'strict'] as const).map((stance) => (
                      <button
                        key={stance}
                        type="button"
                        onClick={() => setEnforcementStance(stance)}
                        className={`py-2 px-1 text-xs font-mono capitalize rounded-lg border text-center transition-all cursor-pointer ${
                          enforcementStance === stance
                            ? 'bg-purple-500/25 border-purple-400 text-purple-200 font-bold ring-1 ring-purple-400/50 shadow-sm'
                            : 'bg-[#171E20] border-[#2C383C] text-gray-400 hover:text-white hover:bg-[#1C2528]'
                        }`}
                      >
                        {stance}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Memo Proforma */}
              <div className="space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <span className="text-xs font-mono font-bold text-gray-300 flex items-center gap-1.5">
                    <FileText size={13} className="text-purple-400 shrink-0" />
                    <span>Generated Scope Defense &amp; Kill-Fee Memo:</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopy(scopeCalculations.scopeMemoProforma, 'scope_memo')}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/30 text-purple-300 rounded-lg text-xs font-mono font-bold cursor-pointer transition-colors active:scale-95 shrink-0 self-start sm:self-auto"
                  >
                    {copiedKey === 'scope_memo' ? (
                      <>
                        <Check size={13} className="text-emerald-400" />
                        <span>Copied Memo!</span>
                      </>
                    ) : (
                      <>
                        <Copy size={13} />
                        <span>Copy Scope Proforma</span>
                      </>
                    )}
                  </button>
                </div>

                <pre className="p-3.5 bg-[#060809] border border-[#222B2E] rounded-xl text-[11px] font-mono text-gray-300 leading-relaxed overflow-x-auto max-h-48 whitespace-pre-wrap select-all">
                  {scopeCalculations.scopeMemoProforma}
                </pre>
              </div>

              {/* Values bar */}
              <div className="p-3 bg-[#0F1416] border border-[#222C2F] rounded-xl flex flex-wrap items-center justify-between gap-3 text-xs font-mono">
                <div>
                  <span className="text-gray-400">Baseline SOW: </span>
                  <span className="text-white font-bold">{sym}{originalSowValue.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-400">Change-Order Expansion: </span>
                  <span className="text-purple-400 font-bold">+{sym}{scopeCalculations.changeOrderValue.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-400">Kill-Fee Indemnity: </span>
                  <span className="text-rose-400 font-bold">{sym}{scopeCalculations.killFeeRetention.toLocaleString()}</span>
                </div>
                <div className="text-sm font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 rounded-lg">
                  Total Adjusted SOW: {sym}{scopeCalculations.totalRevisedValue.toLocaleString()}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* 🚀 4. CALL TO ACTION & TRANSITION INTO LIVE PRIVATE WORKSPACE */}
        <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-gray-400 font-mono flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-teal-400" />
            <span>Tested this scenario? Ready to protect your own business numbers?</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-2.5 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => {
                if (onOpenQuickStart) {
                  onOpenQuickStart();
                } else {
                  onLaunchTrial();
                }
              }}
              className="w-full sm:w-auto bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-black text-xs px-5 py-3 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-[#14B8A6]/25 active:scale-95 transition-all cursor-pointer group"
            >
              <Zap size={14} className="fill-black text-black" />
              <span>Launch This in My Private Sandbox (7-Day Trial)</span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>

            {onLoadDemo && (
              <button
                type="button"
                onClick={onLoadDemo}
                className="w-full sm:w-auto bg-[#141A1C] hover:bg-[#1D2528] border border-[#2B3539] text-gray-300 hover:text-white font-bold text-xs px-4 py-3 rounded-xl flex items-center justify-center gap-1.5 transition-all cursor-pointer"
              >
                <Sliders size={13} className="text-teal-400" />
                <span>Load Full Enterprise Demo</span>
              </button>
            )}
          </div>
        </div>

        {/* ⚖️ 5. INLINE STATUTORY SAFEGUARD COMPLIANCE NOTICE */}
        <div className="mt-3 pt-3 border-t border-[#1C2326] flex items-start gap-2.5 text-[10px] font-mono text-gray-400 leading-relaxed">
          <Scale size={14} className="text-amber-400/80 shrink-0 mt-0.5" />
          <p>
            <strong className="text-gray-300">Fiduciary &amp; Cross-Border Statutory Airlock (NFA Notice):</strong> Indicative operating models are computed strictly from jurisdiction presets ({jur.taxAuthority}). AzuraOS prevents co-mingling and debt defaults with zero bank credentials, zero Plaid reliance, and zero model training on your financial records.
          </p>
        </div>

      </div>

    </div>
  );
}
